import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Nav } from 'reactstrap';

import { ENROLLMENT_TYPE, MEMBERSHIP_TYPE, SCOPES } from '@techchange/components';

import NavSearch from '../NavItems/NavSearch';
import NotificationsNavDropdown from '../NavItems/NotificationsNavDropdown';
import PointsNavDropdown from '../NavItems/PointsNavDropdown';
import UserNavDropdown from '../NavItems/UserNavDropdown';
import usePointSettings from '../hooks/usePointSettings';
import usePointTotal from '../hooks/usePointTotal';

import styles from './LoggedInNav.module.scss';

const propTypes = {

	/**
	 * The current user.
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user's display name.
		 */
		display_name: PropTypes.string.isRequired,

		/**
		 * The profile pictures for the user.
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * True, if the image is active.
			 */
			active: PropTypes.bool,

			/**
			 * The filstack image url.
			 */
			image: PropTypes.string,

			/**
			 * The filestack security policy.
			 */
			policy: PropTypes.string,

			/**
			 * The filestack security signature.
			 */
			signature: PropTypes.string,

		})).isRequired,

		/**
		 * The user's enrollment in the active course session (if any).
		 */
		enrollment: PropTypes.shape({

			/**
			 * The enrollment type.
			 */
			type: PropTypes.oneOf([
				ENROLLMENT_TYPE.GUEST,
				ENROLLMENT_TYPE.STUDENT,
				ENROLLMENT_TYPE.GUEST_EXPERT,
				ENROLLMENT_TYPE.FACILITATOR,
				ENROLLMENT_TYPE.CONTENT_EDITOR,
				ENROLLMENT_TYPE.ADMIN
			]),

		}),

		/**
		 * The user's membership in the organization.
		 */
		membership: PropTypes.shape({

			/**
			 * The membership type.
			 */
			type: PropTypes.oneOf([
				MEMBERSHIP_TYPE.STUDENT,
				MEMBERSHIP_TYPE.ADMIN,
				MEMBERSHIP_TYPE.OWNER,
			]),

		}).isRequired,

	}).isRequired,

	/**
	 * The organization for the current user.
	 */
	myOrganization: PropTypes.shape({

		/**
		 * The organization ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The organization title.
		 */
		title: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The active course session (if any).
	 */
	session: PropTypes.shape({

		/**
		 * The session ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The session slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The active course (if any).
		 */
		course: PropTypes.shape({

			/**
			 * The course ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The course title.
			 */
			title: PropTypes.string.isRequired,

		}),

	}),

	/**
	 * The course slug (i.e. base URL + session slug) to use as prefix for session specific links.
	 */
	courseSlug: PropTypes.string,

	/**
	 * NOTE: Temporary flag to hide fields related to automatic
	 * translation.
	 */
	hideTranslationFields: PropTypes.bool,

	/**
	 * Callback to handle updating the user's language preference.
	 */
	onSelectLanguage: PropTypes.func,

};

const LoggedInNav = (props) => {

	const {
		currentUser,
		myOrganization,
		session,
		courseSlug,
		hideTranslationFields,
		onSelectLanguage
	} = props;

	// Check if points are enabled for the organization
	const { pointsEnabled: orgPointsEnabled, pointLabel } = usePointSettings({
		scope: SCOPES.ORGANIZATION,
		scope_id: myOrganization.id,
	});

	// Keep track of the org-wide point total for the user (on initial page load)
	const [orgPointTotal, fetchOrgPointTotal] = usePointTotal({
		user_id: currentUser.id,
		scope: SCOPES.ORGANIZATION,
		scope_id: myOrganization.id,
		organization_id: myOrganization.id,
	});

	// Check if the current user is an admin for the organization as a whole
	const isAdmin =
		currentUser?.membership?.type === MEMBERSHIP_TYPE.ADMIN ||
		currentUser?.membership?.type === MEMBERSHIP_TYPE.OWNER;

	// Check if the current user has admin privileges for the active course session (if any)
	const isSessionAdmin =
		currentUser?.enrollment?.type === ENROLLMENT_TYPE.FACILITATOR ||
		currentUser?.enrollment?.type === ENROLLMENT_TYPE.CONTENT_EDITOR ||
		currentUser?.enrollment?.type === ENROLLMENT_TYPE.ADMIN;

	/**
	 * Fetch point labels and point totals
	 */
	useEffect(() => {

		// Do not fetch point information if points are disabled for the organization
		if (!orgPointsEnabled) { return; }

		// Fetch the user's org-wide point total
		fetchOrgPointTotal();

	}, [orgPointsEnabled, currentUser.id, myOrganization.id, fetchOrgPointTotal]);

	return (
		<Nav
			className={styles['tc-universal-navbar-logged-in-nav']}
			navbar>
			<NavSearch
				currentUser={currentUser}
				isAdmin={isAdmin} />
			{orgPointsEnabled &&
				<PointsNavDropdown
					currentUser={currentUser}
					pointLabel={pointLabel}
					orgPointTotal={orgPointTotal} />
			}
			<NotificationsNavDropdown
				currentUser={currentUser}
				myOrganization={myOrganization} />
			<UserNavDropdown
				currentUser={currentUser}
				myOrganization={myOrganization}
				isAdmin={isAdmin}
				isSessionAdmin={isSessionAdmin}
				session={session}
				courseSlug={courseSlug}
				pointLabel={pointLabel}
				hideTranslationFields={hideTranslationFields}
				onSelectLanguage={onSelectLanguage} />
		</Nav>
	);

};

LoggedInNav.propTypes = propTypes;

export default LoggedInNav;
